// src/app/_service/task-assignee.service.ts
// Who an inline task can be assigned to (tasks/frontend-inline-tasks-phase-1-requirements.md): the
// members of the Note's Space — GET /api/spaces/:id/members — and nobody else.
//
// Offline-first: the last member list fetched for each Space is cached in localStorage (account-scoped,
// like SpaceDirectoryService), and is emitted straight away while the server is asked again. A
// private Space falls back to its owner (from the cached Space directory) when nothing is cached yet.
// When neither is available the list is `null` — unknown — so no owner gets flagged as ineligible
// just because this device is offline.

import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { SpaceService } from './space.service';
import { SpaceDirectoryService } from './space-directory.service';
import { AuthService } from './auth.service';
import { targetSuffix } from '../_util/data-target';
import { TaskAssignee } from '../_util/inline-task.util';

const TASK_ASSIGNEES_KEY_PREFIX = 'notes:task_assignees';
/** A server list younger than this is reused instead of asking again (one request per Space while
 * moving between notes, not one per note). */
const FRESH_FOR_MS = 5 * 60 * 1000;

export type TaskAssigneeSource = 'server' | 'cache' | 'fallback' | 'unavailable';

export interface TaskAssigneeList {
  /** The Space the list is for (a note without a spaceId is in the user's private Space). */
  spaceId: string | null;
  /** null = not known (offline, never loaded): don't treat anyone as ineligible. */
  assignees: TaskAssignee[] | null;
  source: TaskAssigneeSource;
}

@Injectable({ providedIn: 'root' })
export class TaskAssigneeService {
  private readonly spaceService = inject(SpaceService);
  private readonly directory = inject(SpaceDirectoryService);
  private readonly auth = inject(AuthService);
  private readonly fetchedAt = new Map<string, number>();

  private get storageKey(): string {
    return `${TASK_ASSIGNEES_KEY_PREFIX}${targetSuffix()}::${this.auth.getUsername() ?? 'anonymous'}`;
  }

  private readCache(): Record<string, TaskAssignee[]> {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.storageKey) ?? '{}');
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  private writeCache(spaceId: string, assignees: TaskAssignee[]): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({ ...this.readCache(), [spaceId]: assignees }));
    } catch {
      // Storage full/unavailable — this session still has the list in memory.
    }
  }

  /** The note's Space, or the user's private Space for a note without one. */
  resolveSpaceId(spaceId: string | null | undefined): string | null {
    return spaceId || this.directory.spaces.find((s) => s.type === 'PRIVATE')?.id || null;
  }

  /** A private Space is only accessible to its owner. */
  private fallbackFor(spaceId: string): TaskAssignee[] | null {
    const space = this.directory.spaces.find((s) => s.id === spaceId);
    if (space?.type !== 'PRIVATE' || !space.owner_user_id) return null;
    return [{ id: space.owner_user_id, name: space.owner_username || space.owner_user_id }];
  }

  /** The eligible assignees for a note's Space: the cached list (if any) at once, then the server's.
   * Never errors — a failed/offline fetch just keeps what was emitted. */
  assignees$(spaceId: string | null | undefined): Observable<TaskAssigneeList> {
    return new Observable<TaskAssigneeList>((subscriber) => {
      const id = this.resolveSpaceId(spaceId);
      if (!id) {
        subscriber.next({ spaceId: null, assignees: null, source: 'unavailable' });
        subscriber.complete();
        return undefined;
      }

      const cached = this.readCache()[id];
      const fallback = cached ? null : this.fallbackFor(id);
      if (cached) subscriber.next({ spaceId: id, assignees: cached, source: 'cache' });
      else if (fallback) subscriber.next({ spaceId: id, assignees: fallback, source: 'fallback' });

      if (cached && Date.now() - (this.fetchedAt.get(id) ?? 0) < FRESH_FOR_MS) {
        subscriber.complete();
        return undefined;
      }

      const request = this.spaceService.getMembers(id).subscribe({
        next: (members) => {
          const assignees = members
            .filter((m) => !!m.user_id)
            .map((m) => ({ id: m.user_id, name: m.username || m.user_id }))
            .sort((a, b) => a.name.localeCompare(b.name));
          this.fetchedAt.set(id, Date.now());
          this.writeCache(id, assignees);
          subscriber.next({ spaceId: id, assignees, source: 'server' });
          subscriber.complete();
        },
        error: () => {
          if (!cached && !fallback) subscriber.next({ spaceId: id, assignees: null, source: 'unavailable' });
          subscriber.complete();
        },
      });
      return () => request.unsubscribe();
    });
  }
}
